// @flow

import { combineReducers } from 'redux';
import type { ExtractReturn } from '@ta/common.types';

import type { RouteContext } from '@ta/lithium.context';
import type { State as ApiState, Action as ApiAction } from './api';
import api, { newApiRequests, newApiResponse, requestApiRebind } from './api';
import type { State as I18nState, Action as I18nAction } from './i18n';
import i18n, { changeLocale } from './i18n';
import type { State as PageState, Action as PageAction } from './page';
import page, { pageActions } from './page';
import type { State as TravelerInfoState } from './travelerInfo';
import type { State as PrivateStateState, Action as PrivateStateAction } from './private';
import { REDUCER as privateStateReducer, PrivateStateAccessor } from './private';
import {
  setEatTravelerDate,
  setEatTravelerTime,
  setEatTravelerPartySize,
  setEatTravelerDefaultStatus,
} from './travelerInfo/restaurants';
import { setVrTravelerInfo } from './travelerInfo/vr';
import {
  setHotelsTravelerInfo,
  setTravelerTypeInfo,
  setTTIOpenStatus,
} from './travelerInfo/hotels';
import { setHomeTravelerInfo } from './travelerInfo/home';
import { setFlightsTravelerInfo } from './travelerInfo/flights';
import travelerInfo from './travelerInfo';
import type { State as AuthState, Action as AuthAction } from './auth';
import auth, { setLoggedInUserId } from './auth';
import type { State as MetaState } from './meta';
import meta, {
  viewportCategoryChange,
  type ViewportCategory as InnerViewportCategory,
  type UserAgentCategory as InnerUserAgentCategory,
} from './meta';
import type { State as TrackingState } from './tracking';
import tracking from './tracking';
import type { State as RouteState } from './route';
import route, { navigate, type Action as RouteAction } from './route';
import lithiumRoute from './lithiumRoute';
import type { State as OverlaysState, Action as OverlaysAction } from './overlays';
import overlays, {
  showGlobalOverlay,
  hideGlobalOverlay,
  showTooltip,
  showFab,
  showLocalOverlay,
  hideLocalOverlay,
  launchToast,
  launchExclusiveToast,
  removeToast,
} from './overlays';

// eslint-disable-next-line import/no-cycle
import initializeStore, { getStore, resizeHandler as storeResizeHandler } from './store';

export type { BrowserType, OSType } from './meta';
export type ViewportCategory = InnerViewportCategory;
export type UserAgentCategory = InnerUserAgentCategory;

export type State = {|
  i18n: I18nState,
  api: ApiState,
  page: PageState,
  travelerInfo: TravelerInfoState,
  auth: AuthState,
  overlays: OverlaysState,
  meta: MetaState,
  tracking: TrackingState,
  route: RouteState,
  lithiumRoute: RouteContext,
  private: PrivateStateState,
|};

export const SSR_RELOAD_ACTION = 'SSR_RELOAD';
const ssrReloadAction = (newState: State) => ({
  type: SSR_RELOAD_ACTION,
  newState,
});

export const BATCH_ACTION = 'BATCH';
// eslint-disable-next-line no-use-before-define
export const batchAction = (actions: Array<Action>) => ({
  type: BATCH_ACTION,
  actions,
});

export const PAUSE_REQUEST_ACTION = 'PAUSE_REQUEST';
export const pauseRequestAction = () => ({
  type: PAUSE_REQUEST_ACTION,
});

export const RESUME_REQUEST_ACTION = 'RESUME_REQUEST';
export const resumeRequestAction = () => ({
  type: RESUME_REQUEST_ACTION,
});

export type Action =
  | I18nAction
  | ApiAction
  | PageAction
  | OverlaysAction
  | RouteAction
  | AuthAction
  | PrivateStateAction
  | ExtractReturn<typeof ssrReloadAction>
  | ExtractReturn<typeof batchAction>
  | ExtractReturn<typeof pauseRequestAction>
  | ExtractReturn<typeof resumeRequestAction>;

export const actions = {
  api: {
    newApiRequests,
    newApiResponse,
    requestApiRebind,
  },
  i18n: {
    changeLocale,
  },
  meta: {
    viewportCategoryChange,
  },
  overlays: {
    showGlobalOverlay,
    hideGlobalOverlay,
    showFab,
    showTooltip,
    showLocalOverlay,
    hideLocalOverlay,
    launchToast,
    launchExclusiveToast,
    removeToast,
  },
  route: {
    navigate,
  },
  travelerInfo: {
    setEatTravelerDate,
    setEatTravelerTime,
    setEatTravelerPartySize,
    setEatTravelerDefaultStatus,
    setHotelsTravelerInfo,
    setTravelerTypeInfo,
    setTTIOpenStatus,
    setVrTravelerInfo,
    setHomeTravelerInfo,
    setFlightsTravelerInfo,
  },
  auth: {
    setLoggedInUserId,
  },
  page: pageActions,
};

export { initializeStore, getStore, storeResizeHandler, PrivateStateAccessor };

const subReducers = combineReducers<any, State>({
  i18n,
  api,
  page,
  travelerInfo,
  auth,
  overlays,
  meta,
  tracking,
  route,
  lithiumRoute,
  private: privateStateReducer,
});

const reducer = (state: ?State, action: Action): State => {
  if (action.type === BATCH_ACTION) {
    return action.actions.reduce(reducer, state);
  }
  if (action.type === SSR_RELOAD_ACTION) {
    if (!state) {
      return action.newState;
    }
    return {
      i18n: action.newState.i18n || state.i18n,
      api: action.newState.api || state.api,
      page: action.newState.page || state.page,
      travelerInfo: action.newState.travelerInfo || state.travelerInfo,
      auth: action.newState.auth || state.auth,
      overlays: state.overlays,
      meta: action.newState.meta || state.meta,
      tracking: action.newState.tracking || state.tracking,
      route: action.newState.route || state.route,
      lithiumRoute: action.newState.lithiumRoute || state.lithiumRoute,
      private: privateStateReducer(state.private, action),
    };
  }
  return subReducers((state: any), action);
};

export default reducer;
